import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import type { JsonObject } from "@/lib/json";
import { WHITEBOARD_CLIENT_HEADER } from "@/lib/whiteboard/live";
import {
  fileRef,
  WHITEBOARD_IMAGE_TYPES,
  type WhiteboardDataFile,
  type WhiteboardImageType,
  type WhiteboardPermission,
  type WhiteboardStoredFile,
  whiteboardDetailSchema,
  whiteboardListItemSchema,
  whiteboardShareEntrySchema,
  whiteboardSummarySchema,
  type WhiteboardDetail,
  type WhiteboardSummary,
  type WhiteboardUpdate,
} from "@/lib/whiteboard/schema";

export class WhiteboardRequestError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function request(url: string, init?: RequestInit) {
  const response = await fetch(url, init).catch(() => {
    throw new Error("Connexion au serveur impossible. Réessayez.");
  });

  if (!response.ok) {
    const error = z
      .object({ message: z.string() })
      .safeParse(await response.json().catch(() => null));

    throw new WhiteboardRequestError(
      response.status,
      error.success ? error.data.message : "Opération impossible. Réessayez.",
    );
  }

  return response;
}

export const json = (method: string, body: JsonObject, init?: RequestInit): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
  ...init,
});

const path = (id: string, rest = "") => `/api/whiteboards/${encodeURIComponent(id)}${rest}`;

export const whiteboardsQueryOptions = () =>
  queryOptions({
    queryKey: ["whiteboards", "list"],
    queryFn: async () =>
      z.array(whiteboardListItemSchema).parse(await (await request("/api/whiteboards")).json()),
  });

export const whiteboardQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["whiteboards", "detail", id],
    queryFn: async () => whiteboardDetailSchema.parse(await (await request(path(id))).json()),
    enabled: Boolean(id),
    retry: false,
  });

export const whiteboardSharesQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["whiteboards", "shares", id],
    queryFn: async () =>
      z.array(whiteboardShareEntrySchema).parse(await (await request(path(id, "/shares"))).json()),
    retry: false,
  });

/** The stored preview; `previewAt` versions the URL so the browser may keep it. */
export const whiteboardPreviewUrl = (id: string, previewAt: string) =>
  `${path(id, "/preview")}?v=${encodeURIComponent(previewAt)}`;

export async function createWhiteboard(input: {
  title: string;
  sourceId?: string;
  projectId?: string;
}) {
  return whiteboardDetailSchema.parse(
    await (await request("/api/whiteboards", json("POST", input))).json(),
  );
}

/** `client`: the live channel's tab, so the save is not announced back to it. */
export async function saveWhiteboard(id: string, update: WhiteboardUpdate, client?: string | null) {
  const init = json("PATCH", update);

  if (client) init.headers = { ...init.headers, [WHITEBOARD_CLIENT_HEADER]: client };

  return whiteboardSummarySchema.parse(await (await request(path(id), init)).json());
}

export async function removeWhiteboard(id: string, revision: number, init?: RequestInit) {
  await request(path(id), json("DELETE", { revision }, init));
}

export async function discardEmptyWhiteboard(id: string, revision: number) {
  await request(path(id), json("DELETE", { revision, discardEmpty: true }, { keepalive: true }));
}

export async function saveWhiteboardPreview(
  id: string,
  body: { revision: number; preview: string | null },
) {
  return z
    .object({ previewAt: z.iso.datetime().nullable() })
    .parse(await (await request(path(id, "/preview"), json("PUT", body))).json());
}

export async function changeWhiteboardShare(
  id: string,
  method: "POST" | "PATCH" | "DELETE",
  userId: string,
  permission?: WhiteboardPermission,
) {
  const body: JsonObject = { userId };

  if (permission) body.permission = permission;

  return z
    .array(whiteboardShareEntrySchema)
    .parse(await (await request(path(id, "/shares"), json(method, body))).json());
}

// The list and an opened board share these caches. Each write updates both,
// so an editor opened from the list starts from the latest saved revision.
export function cacheSavedWhiteboard(
  queryClient: QueryClient,
  saved: WhiteboardSummary,
  change: Omit<WhiteboardUpdate, "revision"> = {},
) {
  queryClient.setQueryData(whiteboardQueryOptions(saved.id).queryKey, (previous) =>
    previous
      ? {
          ...previous,
          ...saved,
          scene: change.scene ?? previous.scene,
          library: change.library ?? previous.library,
        }
      : previous,
  );
  queryClient.setQueryData(whiteboardsQueryOptions().queryKey, (previous) =>
    previous
      ?.map((item) =>
        item.id === saved.id
          ? {
              ...item,
              ...saved,
              elementCount: change.scene?.elements.length ?? item.elementCount,
            }
          : item,
      )
      // Same order as the server: most recently edited first.
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
  );
}

export function cacheCreatedWhiteboard(queryClient: QueryClient, board: WhiteboardDetail) {
  queryClient.setQueryData(whiteboardQueryOptions(board.id).queryKey, board);
  void queryClient.invalidateQueries(whiteboardsQueryOptions());
}

export function cacheRemovedWhiteboard(queryClient: QueryClient, id: string) {
  queryClient.setQueryData(whiteboardsQueryOptions().queryKey, (previous) =>
    previous?.filter((item) => item.id !== id),
  );
  queryClient.removeQueries({ queryKey: whiteboardQueryOptions(id).queryKey });
}

export function cacheWhiteboardPreview(
  queryClient: QueryClient,
  id: string,
  revision: number,
  previewAt: string | null,
) {
  queryClient.setQueryData(whiteboardQueryOptions(id).queryKey, (previous) =>
    previous && (previous.previewRevision ?? 0) <= revision
      ? { ...previous, previewRevision: revision }
      : previous,
  );
  queryClient.setQueryData(whiteboardsQueryOptions().queryKey, (previous) =>
    previous?.map((item) =>
      item.id === id && item.revision === revision ? { ...item, previewAt } : item,
    ),
  );
}

// ---- Images ----------------------------------------------------------------
//
// A saved scene names its images (`{ id, mimeType, created }`); their bytes are
// read from the board's file route. The editor itself only takes data URLs: a
// plain URL in its files would leak into exports, the clipboard and SVGs.

/** Where a scene's images are read: the reader's own access, the admin viewer, or the link. */
export type WhiteboardFileSource =
  | { kind: "personal" | "admin"; boardId: string }
  | { kind: "public"; token: string };

export function whiteboardFileUrl(source: WhiteboardFileSource, fileId: string) {
  const file = `/files/${encodeURIComponent(fileId)}`;

  switch (source.kind) {
    case "personal":
      return path(source.boardId, file);
    case "admin":
      return `/api/admin/whiteboards/${encodeURIComponent(source.boardId)}${file}`;
    case "public":
      return `/api/public/whiteboards/${encodeURIComponent(source.token)}${file}`;
  }
}

const imageTypeSchema = z.enum(WHITEBOARD_IMAGE_TYPES);

/** Base64 without FileReader, in 32 KiB slices so the argument list stays bounded. */
function base64Of(bytes: Uint8Array) {
  let binary = "";

  for (let offset = 0; offset < bytes.length; offset += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));

  return btoa(binary);
}

/** One stored image as a data URL. Its bytes never change under an id, so it is read once. */
export const whiteboardFileQueryOptions = (source: WhiteboardFileSource, fileId: string) =>
  queryOptions({
    queryKey: [
      "whiteboards",
      "file",
      source.kind,
      source.kind === "public" ? source.token : source.boardId,
      fileId,
    ] as const,
    queryFn: async (): Promise<{ mimeType: WhiteboardImageType; dataURL: string }> => {
      const response = await request(
        whiteboardFileUrl(source, fileId),
        source.kind === "public" ? { cache: "no-store", credentials: "omit" } : undefined,
      );

      const mimeType = imageTypeSchema.parse(response.headers.get("content-type")?.split(";")[0]);
      const bytes = new Uint8Array(await response.arrayBuffer());

      return { mimeType, dataURL: `data:${mimeType};base64,${base64Of(bytes)}` };
    },
    staleTime: Infinity,
    retry: false,
  });

/**
 * The scene's images as the editor takes them. An image that cannot be read
 * is left out, and the editor draws its placeholder.
 */
export async function hydrateWhiteboardFiles(
  queryClient: QueryClient,
  source: WhiteboardFileSource,
  files: readonly WhiteboardStoredFile[],
): Promise<WhiteboardDataFile[]> {
  const resolved = await Promise.all(
    files.map(async (file) => {
      try {
        const { mimeType, dataURL } = await queryClient.fetchQuery(
          whiteboardFileQueryOptions(source, file.id),
        );

        return { ...fileRef(file), mimeType, dataURL };
      } catch {
        return null;
      }
    }),
  );

  return resolved.filter((file) => file !== null);
}

/** A stored scene with its images read: what the editor, previews and exports draw. */
export async function hydrateScene<Scene extends { files: readonly WhiteboardStoredFile[] }>(
  queryClient: QueryClient,
  source: WhiteboardFileSource,
  scene: Scene,
): Promise<Omit<Scene, "files"> & { files: WhiteboardDataFile[] }> {
  return { ...scene, files: await hydrateWhiteboardFiles(queryClient, source, scene.files) };
}

function bytesOf(dataURL: string) {
  const binary = atob(dataURL.slice(dataURL.indexOf(",") + 1).replace(/\s/g, ""));
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);

  return bytes;
}

/** Uploads one image of the board; the server answers with the type it read from the bytes. */
export async function uploadWhiteboardFile(boardId: string, file: WhiteboardDataFile) {
  const response = await request(path(boardId, `/files/${encodeURIComponent(file.id)}`), {
    method: "PUT",
    headers: { "Content-Type": file.mimeType },
    body: bytesOf(file.dataURL),
  });

  return z
    .object({ id: z.string(), mimeType: imageTypeSchema, size: z.number() })
    .parse(await response.json());
}
